// ─── utils/monitoring.js ─────────────────────────────────────────────────────
// Error monitoring (Sentry) and product analytics (PostHog) for the browser.
// Each is off unless its key is set at build time (README: frontend env), so
// local runs and forks send nothing.
//
// Analytics records page views (including client-side route changes) and the
// signed-in user's id, never their email or name. No autocapture of clicks and
// no session recording. The signup-to-payment funnel is built in PostHog from
// page views alone: /signup, /username (every new account lands there),
// ?paid=1 (every Stripe checkout return) and /academy/payment-success.
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;

export function initMonitoring() {
  if (SENTRY_DSN) {
    Sentry.init({ dsn: SENTRY_DSN, environment: process.env.NODE_ENV, sendDefaultPii: false, tracesSampleRate: 0 });
  }
  if (POSTHOG_KEY) {
    posthog.init(POSTHOG_KEY, {
      api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com',
      capture_pageview: 'history_change',
      autocapture: false,
      disable_session_recording: true,
      // PostHog switches these on from the project's own settings; page
      // views are all the privacy policy describes, so they stay off here.
      capture_dead_clicks: false,
      enable_heatmaps: false,
      disable_surveys: true,
      capture_performance: false,
      person_profiles: 'identified_only',
    });
  }
}

// Ties events to an account by id only; null on sign-out starts a fresh visitor.
export function identifyUser(id) {
  if (SENTRY_DSN) Sentry.setUser(id ? { id: String(id) } : null);
  if (!POSTHOG_KEY) return;
  if (id) posthog.identify(String(id));
  else posthog.reset();
}

export function reportError(error) {
  if (SENTRY_DSN) Sentry.captureException(error);
}
